import dot from 'dot-prop-immutable';
import type { ValueOf } from 'type-fest';

import { type RootState } from 'store/configureStore';
import type { ActionWithPayload } from 'store/types';

import {
  type SuggestionAnnotationCommand,
  type SuggestionAnnotationStateMap,
} from '../annotationCommands';
import { SUGGESTION_ANNOTATION_COMMAND_ORIGINS } from '../annotationCommands/consts';
import { types } from './actions';
import type { ReadOnlyAnnotation } from './types';

type SuggestionAnnotationsViewerSlice = {
  commands: SuggestionAnnotationCommand[];
  stateMap: SuggestionAnnotationStateMap;
  focusedAnnotationOrigin: ValueOf<
    typeof SUGGESTION_ANNOTATION_COMMAND_ORIGINS
  > | null;
};

type PdfViewerState = {
  annotationRemovalQueue: string[];
  currentPageNumber: number;
  jumpToAnnotationId: string | null;
  searchQuery: string | null;
  readOnlyAnnotationIds: string[];
  readOnlyAnnotationRenderQueue: ReadOnlyAnnotation[];
  suggestionAnnotationsState: SuggestionAnnotationsViewerSlice;
  manuallyScaleMeasurementToolsRequest: boolean;
  viewerAnnotationIds: string[];
  viewerAnnotationZoom: Record<string, unknown>;
  viewerAnnotations: Record<string, unknown>;
  viewerMeasurementIds: string[];
  viewerMeasurementZoom: Record<string, unknown>;
  viewerMeasurements: Record<string, unknown>;
  isApplyRedactionsModalOpen: boolean;
  redactFileRequest: boolean;
  rerenderAllMeasurementAnnotationsRequest: boolean;
  isLeftPanelOpen: boolean;
};

type PdfViewerAction =
  | ActionWithPayload<
      typeof types.APPLY_REDACTIONS_REQUEST,
      { fileName: string }
    >
  | ActionWithPayload<typeof types.APPLY_REDACTIONS_ERROR, { error: unknown }>
  | ActionWithPayload<typeof types.APPLY_REDACTIONS_SUCCESS>
  | ActionWithPayload<typeof types.CLOSE_APPLY_REDACTIONS_MODAL>
  | ActionWithPayload<typeof types.OPEN_APPLY_REDACTIONS_MODAL>
  | ActionWithPayload<typeof types.PDF_SEARCH_REQUEST, { query: string }>
  | ActionWithPayload<typeof types.PDF_SEARCH_SUCCESS>
  | ActionWithPayload<typeof types.PDF_VIEWER_RESET>
  | ActionWithPayload<
      typeof types.RENDER_READ_ONLY_ANNOTATIONS_REQUEST,
      { readOnlyAnnotations: Array<{ id: string }> }
    >
  | ActionWithPayload<
      typeof types.RENDER_READ_ONLY_ANNOTATIONS_SUCCESS,
      { annotationIds: string[] }
    >
  | ActionWithPayload<
      typeof types.JUMP_TO_ANNOTATION_REQUEST,
      { annotationId: string }
    >
  | ActionWithPayload<typeof types.JUMP_TO_ANNOTATION_SUCCESS>
  | ActionWithPayload<typeof types.MANUALLY_SCALE_MEASUREMENT_TOOLS_SUCCESS>
  | ActionWithPayload<
      typeof types.REMOVE_ANNOTATIONS_REQUEST,
      { annotationIds: string[] }
    >
  | ActionWithPayload<
      typeof types.REMOVE_ANNOTATIONS_SUCCESS,
      { annotationIds: string[] }
    >
  | ActionWithPayload<
      typeof types.UPDATE_CURRENT_PAGE_NUMBER,
      { pageNumber: number }
    >
  | ActionWithPayload<typeof types.UPDATE_PAGE_SCALE_ERROR, { error: unknown }>
  | ActionWithPayload<typeof types.UPDATE_PAGE_SCALE_SUCCESS>
  | ActionWithPayload<typeof types.RERENDER_ALL_PAGE_SCALES>
  | ActionWithPayload<typeof types.RERENDER_ALL_MEASUREMENT_ANNOTATIONS_SUCCESS>
  | ActionWithPayload<
      typeof types.VIEWER_ANNOTATIONS_ADD,
      { id: string; zoom: unknown; xfdfString: string }
    >
  | ActionWithPayload<typeof types.VIEWER_ANNOTATIONS_DELETE, { ids: string[] }>
  | ActionWithPayload<
      typeof types.VIEWER_ANNOTATIONS_MODIFY,
      { id: string; xfdfString: string; zoom: unknown }
    >
  | ActionWithPayload<typeof types.VIEWER_ANNOTATIONS_REMOVE_ALL>
  | ActionWithPayload<
      typeof types.VIEWER_MEASUREMENTS_ADD,
      { id: string; zoom: unknown; xfdfString: string }
    >
  | ActionWithPayload<
      typeof types.VIEWER_MEASUREMENTS_DELETE,
      { ids: string[] }
    >
  | ActionWithPayload<
      typeof types.VIEWER_MEASUREMENTS_MODIFY,
      { id: string; xfdfString: string; zoom: unknown }
    >
  | ActionWithPayload<typeof types.VIEWER_MEASUREMENTS_REMOVE_ALL>
  | ActionWithPayload<
      typeof types.SUGGESTION_ANNOTATION_COMMAND_QUEUE,
      { commands: SuggestionAnnotationCommand[] }
    >
  | ActionWithPayload<
      typeof types.SUGGESTION_ANNOTATION_COMMAND_SUCCESS,
      {
        commandIds: string[];
        nextStateMap: SuggestionAnnotationStateMap;
        countRedraws: number;
        focusedAnnotationOrigin?: ValueOf<
          typeof SUGGESTION_ANNOTATION_COMMAND_ORIGINS
        > | null;
      }
    >;

const initialState = {
  annotationRemovalQueue: [],
  currentPageNumber: 1,
  jumpToAnnotationId: null,
  searchQuery: null,
  readOnlyAnnotationIds: [],
  readOnlyAnnotationRenderQueue: [],
  suggestionAnnotationsState: {
    commands: [],
    stateMap: {},
    focusedAnnotationOrigin: null,
  },
  manuallyScaleMeasurementToolsRequest: false,
  viewerAnnotationIds: [],
  viewerAnnotationZoom: {},
  viewerAnnotations: {},
  viewerMeasurementIds: [],
  viewerMeasurementZoom: {},
  viewerMeasurements: {},
  isApplyRedactionsModalOpen: false,
  redactFileRequest: false,
  rerenderAllMeasurementAnnotationsRequest: false,
  isLeftPanelOpen: false,
} satisfies PdfViewerState;

const reducer = (
  state: PdfViewerState = initialState,
  action: PdfViewerAction,
) => {
  const { payload, type } = action;
  switch (type) {
    case types.APPLY_REDACTIONS_REQUEST:
      return {
        ...state,
        redactFileRequest: true,
      };
    case types.APPLY_REDACTIONS_ERROR:
    case types.APPLY_REDACTIONS_SUCCESS:
      return {
        ...state,
        redactFileRequest: false,
      };
    case types.CLOSE_APPLY_REDACTIONS_MODAL:
      return {
        ...state,
        isApplyRedactionsModalOpen: false,
      };
    case types.OPEN_APPLY_REDACTIONS_MODAL:
      return {
        ...state,
        isApplyRedactionsModalOpen: true,
      };
    case types.PDF_SEARCH_REQUEST:
      return {
        ...state,
        searchQuery: payload.query,
      };
    case types.PDF_SEARCH_SUCCESS:
      return {
        ...state,
        searchQuery: null,
      };
    case types.PDF_VIEWER_RESET:
      return {
        ...initialState,
      };
    case types.RENDER_READ_ONLY_ANNOTATIONS_REQUEST:
      return {
        ...state,
        readOnlyAnnotationIds: [
          ...state.readOnlyAnnotationIds,
          ...payload.readOnlyAnnotations.map(({ id }) => id),
        ],
        readOnlyAnnotationRenderQueue: [
          ...state.readOnlyAnnotationRenderQueue,
          ...payload.readOnlyAnnotations,
        ],
      };
    case types.RENDER_READ_ONLY_ANNOTATIONS_SUCCESS:
      return {
        ...state,
        readOnlyAnnotationRenderQueue:
          state.readOnlyAnnotationRenderQueue.filter(
            ({ id }) => !payload.annotationIds.includes(id),
          ),
        rerenderAllMeasurementAnnotationsRequest: true,
        manuallyScaleMeasurementToolsRequest: true,
      };
    case types.JUMP_TO_ANNOTATION_REQUEST:
      return {
        ...state,
        jumpToAnnotationId: payload.annotationId,
      };
    case types.JUMP_TO_ANNOTATION_SUCCESS:
      return {
        ...state,
        jumpToAnnotationId: null,
      };
    case types.MANUALLY_SCALE_MEASUREMENT_TOOLS_SUCCESS:
      return {
        ...state,
        manuallyScaleMeasurementToolsRequest: false,
      };
    case types.REMOVE_ANNOTATIONS_REQUEST:
      return {
        ...state,
        annotationRemovalQueue: [
          ...state.annotationRemovalQueue,
          ...payload.annotationIds,
        ],
        readOnlyAnnotationIds: state.readOnlyAnnotationIds.filter(
          (id) => !payload.annotationIds.includes(id),
        ),
      };
    case types.REMOVE_ANNOTATIONS_SUCCESS:
      return {
        ...state,
        annotationRemovalQueue: state.annotationRemovalQueue.filter(
          (id) => !payload.annotationIds.includes(id),
        ),
      };
    case types.UPDATE_CURRENT_PAGE_NUMBER:
      return {
        ...state,
        currentPageNumber: payload.pageNumber,
        manuallyScaleMeasurementToolsRequest: true,
      };
    case types.UPDATE_PAGE_SCALE_ERROR:
    case types.UPDATE_PAGE_SCALE_SUCCESS:
    case types.RERENDER_ALL_PAGE_SCALES:
      return {
        ...state,
        rerenderAllMeasurementAnnotationsRequest: true,
        manuallyScaleMeasurementToolsRequest: true,
      };
    case types.RERENDER_ALL_MEASUREMENT_ANNOTATIONS_SUCCESS:
      return {
        ...state,
        rerenderAllMeasurementAnnotationsRequest: false,
      };
    case types.VIEWER_ANNOTATIONS_ADD:
      return {
        ...dot.set(
          state,
          `viewerAnnotations.${payload.id}`,
          payload.xfdfString,
        ),
        viewerAnnotationZoom: {
          ...dot.set(state.viewerAnnotationZoom, `${payload.id}`, payload.zoom),
        },
        viewerAnnotationIds: [...state.viewerAnnotationIds, payload.id],
      };
    case types.VIEWER_ANNOTATIONS_DELETE:
      return {
        ...state,
        readOnlyAnnotationIds: state.readOnlyAnnotationIds.filter(
          (id) => !payload.ids.includes(id),
        ),
        viewerAnnotationIds: state.viewerAnnotationIds.filter(
          (id) => !payload.ids.includes(id),
        ),
      };
    case types.VIEWER_ANNOTATIONS_MODIFY:
      return {
        ...dot.set(
          state,
          `viewerAnnotations.${payload.id}`,
          payload.xfdfString,
        ),
        viewerAnnotationZoom: {
          ...dot.set(state.viewerAnnotationZoom, `${payload.id}`, payload.zoom),
        },
      };
    case types.VIEWER_ANNOTATIONS_REMOVE_ALL:
      return {
        ...state,
        annotationRemovalQueue: state.viewerAnnotationIds,
      };
    case types.VIEWER_MEASUREMENTS_ADD:
      return {
        ...dot.set(
          state,
          `viewerMeasurements.${payload.id}`,
          payload.xfdfString,
        ),
        viewerMeasurementZoom: {
          ...dot.set(
            state.viewerMeasurementZoom,
            `${payload.id}`,
            payload.zoom,
          ),
        },
        viewerMeasurementIds: [...state.viewerMeasurementIds, payload.id],
      };
    case types.VIEWER_MEASUREMENTS_DELETE:
      return {
        ...state,
        readOnlyAnnotationIds: state.readOnlyAnnotationIds.filter(
          (id) => !payload.ids.includes(id),
        ),
        viewerMeasurementIds: state.viewerMeasurementIds.filter(
          (id) => !payload.ids.includes(id),
        ),
      };
    case types.VIEWER_MEASUREMENTS_MODIFY:
      return {
        ...dot.set(
          state,
          `viewerMeasurements.${payload.id}`,
          payload.xfdfString,
        ),
        viewerMeasurementZoom: {
          ...dot.set(
            state.viewerMeasurementZoom,
            `${payload.id}`,
            payload.zoom,
          ),
        },
      };
    case types.VIEWER_MEASUREMENTS_REMOVE_ALL:
      return {
        ...state,
        annotationRemovalQueue: state.viewerMeasurementIds,
      };
    case types.SUGGESTION_ANNOTATION_COMMAND_QUEUE:
      return {
        ...state,
        suggestionAnnotationsState: {
          ...state.suggestionAnnotationsState,
          commands: [
            ...state.suggestionAnnotationsState.commands,
            ...payload.commands,
          ],
        },
      };
    case types.SUGGESTION_ANNOTATION_COMMAND_SUCCESS:
      if (payload.countRedraws > 0 || payload.commandIds.length > 0) {
        return {
          ...state,
          suggestionAnnotationsState: {
            ...state.suggestionAnnotationsState,
            commands:
              payload.commandIds.length > 0
                ? state.suggestionAnnotationsState.commands.filter(
                    ({ id }) => !payload.commandIds.includes(id),
                  )
                : state.suggestionAnnotationsState.commands,
            stateMap:
              payload.countRedraws > 0
                ? {
                    ...state.suggestionAnnotationsState.stateMap,
                    ...payload.nextStateMap,
                  }
                : state.suggestionAnnotationsState.stateMap,
            ...(payload.focusedAnnotationOrigin !== undefined && {
              focusedAnnotationOrigin: payload.focusedAnnotationOrigin,
            }),
          },
        };
      }
      return state;
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): PdfViewerState =>
  state.modules.common.viewer.pdfViewer;

export const getAreUnsavedPdfAnnotationsPresent = (state: RootState) =>
  Boolean(getScope(state).viewerAnnotationIds.length) ||
  Boolean(getScope(state).viewerMeasurementIds.length);

export const getAnnotationRemovalQueue = (state: RootState) =>
  getScope(state).annotationRemovalQueue;

export const getCurrentPageNumber = (state: RootState) =>
  getScope(state).currentPageNumber;

export const getReadOnlyAnnotationRenderQueue = (state: RootState) =>
  getScope(state).readOnlyAnnotationRenderQueue;

export const getSuggestionAnnotationsState = (state: RootState) =>
  getScope(state).suggestionAnnotationsState;

export const getSuggestionAnnotationsCommandQueue = (state: RootState) =>
  getSuggestionAnnotationsState(state).commands;

export const getSuggestionAnnotationsStateMap = (state: RootState) =>
  getSuggestionAnnotationsState(state).stateMap;

export const getSuggestionAnnotationStateById = (
  state: RootState,
  id: string,
) => getSuggestionAnnotationsState(state).stateMap[id];

/**
 * Annotation id for `FocusedExtractionOverlay` when focus was initiated from a
 * finding pill (not from clicking the annotation in the viewer).
 */
export const getFocusedPillExtractionOverlayAnnotationId = (
  state: RootState,
): string | null => {
  const suggestionState = getSuggestionAnnotationsState(state);
  if (
    suggestionState.focusedAnnotationOrigin !==
    SUGGESTION_ANNOTATION_COMMAND_ORIGINS.PILL
  ) {
    return null;
  }
  const focusedEntry = Object.entries(suggestionState.stateMap).find(
    ([, annotationState]) => annotationState?.isFocused,
  );
  return focusedEntry?.[0] ?? null;
};

export const getRerenderAllMeasurementAnnotationsRequest = (state: RootState) =>
  getScope(state).rerenderAllMeasurementAnnotationsRequest;

export const getManualScaleMeasurementToolsRequest = (state: RootState) =>
  getScope(state).manuallyScaleMeasurementToolsRequest;

export const getJumpToAnnotationId = (state: RootState) =>
  getScope(state).jumpToAnnotationId;

export const getSearchQuery = (state: RootState) => getScope(state).searchQuery;

export const getReadOnlyAnnotationIds = (state: RootState) =>
  getScope(state).readOnlyAnnotationIds;

export const getViewerMeasurementIds = (state: RootState) =>
  getScope(state).viewerMeasurementIds;

export const getViewerAnnotationIds = (state: RootState) =>
  getScope(state).viewerAnnotationIds;

export const getViewerAnnotations = (state: RootState) => {
  const activeViewerAnnotationIds = getViewerAnnotationIds(state);
  const viewerAnnotationZoom = getScope(state).viewerAnnotationZoom;
  const viewerAnnotations = getScope(state).viewerAnnotations;
  return activeViewerAnnotationIds.map((id) => ({
    id,
    zoom: viewerAnnotationZoom[id],
    annotation: viewerAnnotations[id],
  }));
};

export const getViewerMeasurements = (state: RootState) => {
  const activeViewerMeasurementIds = getViewerMeasurementIds(state);
  const viewerMeasurementZoom = getScope(state).viewerMeasurementZoom;
  const viewerMeasurements = getScope(state).viewerMeasurements;
  return activeViewerMeasurementIds.map((id) => ({
    id,
    zoom: viewerMeasurementZoom[id],
    annotation: viewerMeasurements[id],
  }));
};

export const getIsApplyRedactionsModalOpen = (state: RootState) =>
  getScope(state).isApplyRedactionsModalOpen;

export const getRedactFileRequest = (state: RootState) =>
  getScope(state).redactFileRequest;
